<template>
  <div class="freight-table-details-page">
    <div class="freight-table-details-page__main">
      <BasePageCard>
        <div class="freight-table-details">
          <a-spin v-if="detailsLoading && !details" class="freight-table-details__spin" />

          <div v-else-if="details">
            <FreightTableSummary :details="details" />

            <a-tabs v-model="activeTab" class="freight-table-details__tabs">
              <a-tab-pane key="rotas" tab="Rotas">
                <FreightTableRoutesFilters
                  @filter-change="onRoutesFilterChange"
                  @open-filters="filtersModalVisible = true"
                />

                <a-spin v-if="routesLoading" class="freight-table-details__spin" />

                <a-empty
                  v-else-if="!routes.length"
                  description="Nenhuma rota encontrada."
                />

                <div v-else>
                  <FreightTableRouteCard
                    v-for="route in routes"
                    :key="route.routeId"
                    :freight-table-id="freightTableId"
                    :route="route"
                    :can-approve="canApproveValidities"
                    :can-cancel="canCancelValidities"
                    :open-validity-id="openValidityId"
                    :can-create="canCreateValidities"
                    :reload-token="reloadTokenFor(route.routeId)"
                    @open-validity="onOpenValidity"
                    @new-validity="openNewValidityPanel"
                  />
                </div>
              </a-tab-pane>

              <a-tab-pane v-if="isReceita" key="clientes" tab="Clientes">
                <FreightTableClientsTable :clients="details.clientes" />
              </a-tab-pane>

              <a-tab-pane key="transportadores" tab="Transportadores">
                <FreightTableCarriersFilters
                  :has-selection="selectedRowKeys.length > 0"
                  @filter-change="onCarriersFilterChange"
                  @open-filters="filtersModalVisible = true"
                  @bulk-action="onCarriersBulkAction"
                />

                <a-empty
                  v-if="showNoCarriers"
                  description="Nenhuma transportadora encontrada."
                />

                <FreightTableCarriersTable
                  v-else
                  :items="carriers"
                  :loading="carriersLoading"
                  :pagination="carriersPagination"
                  :selected-row-keys="selectedRowKeys"
                  @selection-change="selectedRowKeys = $event"
                  @table-change="onCarriersTableChange"
                  @toggle-status="onToggleCarrierStatus"
                />
              </a-tab-pane>

              <a-tab-pane key="historico" tab="Histórico">
                <div class="freight-table-history">
                  <div class="freight-table-history__filters">
                    <BaseHistoryFilters
                      :users="historyUsers"
                      :event-options="historyEventOptions"
                      @change="onHistoryFilterChange"
                    />
                  </div>

                  <div
                    v-if="historyLoading && !history.length"
                    class="freight-table-history__loading"
                  >
                    <a-spin />
                  </div>

                  <a-empty
                    v-else-if="historyLoaded && !history.length"
                    :description="historyEmptyText"
                    class="freight-table-history__empty"
                  />

                  <template v-else>
                    <div
                      v-for="group in historyGroups"
                      :key="group.key"
                      class="freight-table-history__group"
                    >
                      <div class="freight-table-history__date">
                        <span class="freight-table-history__day">
                          {{ group.dayLabel }}
                        </span>
                        <span class="freight-table-history__year">
                          {{ group.yearLabel }}
                        </span>
                      </div>

                      <div
                        v-for="entry in group.items"
                        :key="entry.id"
                        class="freight-table-history__item"
                      >
                        <div class="freight-table-history__item-head">
                          <span class="freight-table-history__avatar">
                            {{ entry.initials }}
                          </span>
                          <span class="freight-table-history__name">
                            {{ entry.userName }}
                          </span>
                          <span class="freight-table-history__time">
                            {{ entry.timestamp }}
                          </span>
                        </div>
                        <p class="freight-table-history__bubble">
                          {{ entry.description }}
                        </p>
                      </div>
                    </div>

                    <a-button
                      v-if="hasMoreHistory"
                      class="freight-table-history__more"
                      block
                      :loading="historyLoading"
                      @click="loadMoreHistory"
                    >
                      Carregar mais
                    </a-button>
                  </template>
                </div>
              </a-tab-pane>
            </a-tabs>

            <FreightTableTabFilterModal
              :visible="filtersModalVisible"
              :filters="activeTabFilters"
              @close="filtersModalVisible = false"
              @apply="onApplyTabFilters"
              @clear="onClearTabFilters"
            />
          </div>
        </div>
      </BasePageCard>
    </div>

    <div
      v-if="details && newValidityPanel.route"
      class="freight-table-details-page__panel-col"
    >
      <FreightTableNewValidityPanel
        :route="newValidityPanel.route"
        :vehicles="vehicleOptions"
        :vehicles-loading="optionsLoading"
        :saving="newValidityPanel.saving"
        :errors="newValidityPanel.errors"
        :tp-calc="details.calculoValor"
        :context="newValidityPanel.context"
        :costs="costOptions"
        @edit="clearNewValidityErrors"
        @close="closeNewValidityPanel"
        @submit="submitNewValidity"
      />
    </div>

    <div
      v-else-if="details && validityPanel.visible"
      class="freight-table-details-page__panel-col"
    >
      <FreightTableValidityPanel
        :validity="validityPanel.validity"
        :details="validityPanel.details"
        :loading="validityPanel.loading"
        @close="closeValidityPanel"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { Modal } from "ant-design-vue";
import BasePageCard from "@/shared/components/BasePageCard.vue";
import FreightTableSummary from "@/modules/FreightTables/components/Details/FreightTableSummary.vue";
import FreightTableCarriersTable from "@/modules/FreightTables/components/Details/FreightTableCarriersTable.vue";
import FreightTableCarriersFilters from "@/modules/FreightTables/components/Details/FreightTableCarriersFilters.vue";
import FreightTableClientsTable from "@/modules/FreightTables/components/Details/FreightTableClientsTable.vue";
import FreightTableRoutesFilters from "@/modules/FreightTables/components/Details/FreightTableRoutesFilters.vue";
import FreightTableRouteCard from "@/modules/FreightTables/components/Details/FreightTableRouteCard.vue";
import FreightTableValidityPanel from "@/modules/FreightTables/components/Details/FreightTableValidityPanel.vue";
import FreightTableNewValidityPanel from "@/modules/FreightTables/components/Details/FreightTableNewValidityPanel.vue";
import FreightTableTabFilterModal from "@/modules/FreightTables/components/Details/FreightTableTabFilterModal.vue";
import BaseHistoryFilters from "@/shared/components/BaseHistoryFilters.vue";
import {
  describeClosedValidity,
  hasNewValidityApiErrors,
  mapNewValidityApiErrors,
  mapNewValidityToApi,
  newValidityModeFor,
} from "@/modules/FreightTables/utils/newValidityForm";
import { FREIGHT_TABLE_TIPO_RECEITA } from "@/modules/FreightTables/constants/freightTableOptions";
import { FREIGHT_TABLE_HISTORY_EVENTS } from "@/modules/FreightTables/constants/freightTableHistoryEvents";
import { FREIGHT_TABLE_STATUS } from "@/modules/FreightTables/constants/freightTableStatus";
import { handleApiError } from "@/shared/utils/handleApiError";
import { isFreightTablePath } from "@/modules/FreightTables/constants/freightTableRoutes";

export default {
  name: "FreightTableDetailsView",
  components: {
    BasePageCard,
    FreightTableSummary,
    FreightTableCarriersTable,
    FreightTableCarriersFilters,
    FreightTableClientsTable,
    FreightTableRoutesFilters,
    FreightTableRouteCard,
    FreightTableValidityPanel,
    FreightTableNewValidityPanel,
    FreightTableTabFilterModal,
    BaseHistoryFilters,
  },
  data() {
    return {
      activeTab: "rotas",
      selectedRowKeys: [],
      hasFetchedCarriers: false,
      carriersRequested: false,
      carriersSearch: "",
      filtersModalVisible: false,
      routesFilters: { sort: null, status: null },
      carriersFilters: { sort: null, status: null },
      validityPanel: {
        visible: false,
        loading: false,
        routeId: null,
        validity: {},
        details: null,
      },
      newValidityPanel: {
        route: null,
        context: {},
        saving: false,
        errors: mapNewValidityApiErrors(null),
      },
      validityReloadTokens: {},
      panelSeq: 0,
      history: [],
      historyMeta: null,
      historyPage: 1,
      historyLoading: false,
      historyLoaded: false,
      historyFilters: null,
      historyUsers: [],
      historyEventOptions: FREIGHT_TABLE_HISTORY_EVENTS,
    };
  },
  computed: {
    ...mapState("freightTables", {
      vehicleOptions: (state) => (state.options && state.options.veiculos) || [],
      costOptions: (state) => (state.options && state.options.custos) || [],
      optionsLoading: (state) => state.optionsLoading,
    }),
    ...mapGetters("freightTables", [
      "details",
      "detailsLoading",
      "carriers",
      "carriersPagination",
      "carriersLoading",
      "routes",
      "routesLoading",
    ]),
    freightTableId() {
      return Number(this.$route.params.id);
    },
    // Só a tabela receita tem cliente: na despesa, os vínculos em tabela_frete_pessoa são as
    // transportadoras da etapa Parceiro.
    isReceita() {
      return Number(this.details.tipoValor) === FREIGHT_TABLE_TIPO_RECEITA;
    },
    openValidityId() {
      return this.validityPanel.visible && this.validityPanel.validity
        ? this.validityPanel.validity.validityId
        : null;
    },
    activeTabFilters() {
      return this.activeTab === "rotas"
        ? this.routesFilters
        : this.carriersFilters;
    },
    showNoCarriers() {
      return (
        this.hasFetchedCarriers &&
        !this.carriersLoading &&
        this.carriersPagination.total === 0
      );
    },
    canApproveValidities() {
      return this.getPermissionFw("tabelafrete.store");
    },
    canCancelValidities() {
      return this.getPermissionFw("tabelafrete.store");
    },
    canCreateValidities() {
      return (
        !!this.details &&
        !this.details.isDraft &&
        !!newValidityModeFor(this.details.calculoValor) &&
        this.getPermissionFw("tabelafrete.rota.update")
      );
    },
    historyGroups() {
      const groups = [];
      const byKey = {};

      this.history.forEach((entry) => {
        if (!byKey[entry.dateKey]) {
          byKey[entry.dateKey] = {
            key: entry.dateKey,
            dayLabel: entry.dayLabel,
            yearLabel: entry.yearLabel,
            items: [],
          };
          groups.push(byKey[entry.dateKey]);
        }
        byKey[entry.dateKey].items.push(entry);
      });

      return groups;
    },
    hasMoreHistory() {
      return (
        !!this.historyMeta &&
        this.historyMeta.current_page < this.historyMeta.last_page
      );
    },
    hasActiveHistoryFilter() {
      const applied = this.historyFilters;
      if (!applied) return false;

      return !!(
        applied.search ||
        (applied.events && applied.events.length) ||
        applied.userId ||
        applied.dateFrom ||
        applied.dateTo
      );
    },
    historyEmptyText() {
      return this.hasActiveHistoryFilter
        ? "Nenhum resultado para os filtros aplicados."
        : "Nenhuma alteração registrada.";
    },
  },
  watch: {
    activeTab(tab) {
      if (tab === "transportadores" && !this.carriersRequested) {
        this.loadCarriers({ page: 1 });
      }
      if (tab === "historico" && !this.historyLoaded && !this.historyLoading) {
        this.loadHistory(1);
      }
    },
  },
  created() {
    this.loadDetails();
    this.loadRoutes();
  },
  beforeDestroy() {
    this.resetDetailsState();
  },
  beforeRouteLeave(to, from, next) {
    if (!isFreightTablePath(to.path)) {
      this.resetListState();
    }
    next();
  },
  methods: {
    ...mapActions("freightTables", [
      "fetchFreightTableDetails",
      "fetchCarriers",
      "updateCarrierStatus",
      "updateCarriersStatus",
      "fetchRoutes",
      "fetchValidityDetails",
      "resetDetailsState",
      "resetListState",
      "fetchFreightTableHistory",
      "fetchFreightTableOptions",
      "createRouteValidity",
      "fetchNewValidityContext",
    ]),
    reloadTokenFor(routeId) {
      return this.validityReloadTokens[routeId] || 0;
    },
    async openNewValidityPanel(route) {
      if (this.newValidityPanel.saving) return;
      this.closeValidityPanel();
      const seq = ++this.panelSeq;

      let context;
      try {
        context = await this.fetchNewValidityContext({
          freightTableId: this.freightTableId,
          routeId: route.routeId,
        });
      } catch (error) {
        if (seq !== this.panelSeq) return;
        handleApiError(this, error, "Não foi possível abrir a nova vigência");
        return;
      }

      if (seq !== this.panelSeq) return;

      this.newValidityPanel = {
        route,
        context,
        saving: false,
        errors: mapNewValidityApiErrors(null),
      };
      this.fetchFreightTableOptions().catch((error) => {
        handleApiError(this, error, "Não foi possível carregar os veículos e custos");
      });
    },
    closeNewValidityPanel() {
      if (this.newValidityPanel.saving) return;
      this.newValidityPanel = {
        route: null,
        context: {},
        saving: false,
        errors: mapNewValidityApiErrors(null),
      };
    },
    clearNewValidityErrors() {
      if (!hasNewValidityApiErrors(this.newValidityPanel.errors)) return;
      this.newValidityPanel = {
        ...this.newValidityPanel,
        errors: mapNewValidityApiErrors(null),
      };
    },
    async submitNewValidity(form) {
      const route = this.newValidityPanel.route;
      if (!route || this.newValidityPanel.saving) return;

      this.newValidityPanel = {
        ...this.newValidityPanel,
        saving: true,
        errors: mapNewValidityApiErrors(null),
      };

      try {
        const result = await this.createRouteValidity({
          freightTableId: this.freightTableId,
          routeId: route.routeId,
          payload: mapNewValidityToApi(form, this.details.calculoValor),
        });

        this.newValidityPanel = { ...this.newValidityPanel, saving: false };
        this.closeNewValidityPanel();
        this.$set(
          this.validityReloadTokens,
          route.routeId,
          this.reloadTokenFor(route.routeId) + 1,
        );
        this.fetchRoutes({ freightTableId: this.freightTableId, silent: true }).catch(
          () => {},
        );
        const several = form.blocos.length > 1;
        this.$notification.success({
          message: several ? "Vigências incluídas" : "Vigência incluída",
          description: [
            several
              ? `As ${form.blocos.length} novas vigências estão pendentes de aprovação.`
              : "A nova vigência está pendente de aprovação.",
            describeClosedValidity(result),
            result && Number(result.rota_status) === FREIGHT_TABLE_STATUS.INATIVA
              ? "A rota foi inativada."
              : "",
          ]
            .filter(Boolean)
            .join(" "),
        });
      } catch (error) {
        const data = error && error.response && error.response.data;
        const errors = mapNewValidityApiErrors(
          data && data.errors,
          form,
          this.vehicleOptions,
        );

        this.newValidityPanel = { ...this.newValidityPanel, saving: false, errors };

        if (!hasNewValidityApiErrors(errors)) {
          handleApiError(this, error, "Não foi possível incluir a vigência");
        }
      }
    },
    onHistoryFilterChange(filters) {
      this.historyFilters = filters;
      this.loadHistory(1);
    },
    async loadHistory(page) {
      if (this.historyLoading) return;

      this.historyLoading = true;
      try {
        const { entries, meta } = await this.fetchFreightTableHistory({
          freightTableId: this.freightTableId,
          page,
          filters: this.historyFilters,
        });

        this.history = page > 1 ? this.history.concat(entries) : entries;
        this.historyMeta = meta;
        this.historyPage = meta ? meta.current_page : page;
        this.historyUsers = meta && meta.availableUsers ? meta.availableUsers : [];
        this.historyLoaded = true;
      } catch (error) {
        handleApiError(this, error, "Não foi possível carregar o histórico");
      } finally {
        this.historyLoading = false;
      }
    },
    loadMoreHistory() {
      this.loadHistory(this.historyPage + 1);
    },
    loadRoutes(payload) {
      this.fetchRoutes({
        freightTableId: this.freightTableId,
        ...payload,
      }).catch((error) => {
        handleApiError(this, error, "Não foi possível carregar as rotas");
      });
    },
    onRoutesFilterChange({ search }) {
      this.loadRoutes({ search });
    },
    onApplyTabFilters({ sort, status }) {
      this.filtersModalVisible = false;
      this.applyTabFilters({ sort, status });
    },
    onClearTabFilters() {
      this.filtersModalVisible = false;
      this.applyTabFilters({ sort: null, status: null });
    },
    applyTabFilters({ sort, status }) {
      if (this.activeTab === "rotas") {
        this.routesFilters = { sort, status };
        this.loadRoutes({ sort, status });
        return;
      }

      this.carriersFilters = { sort, status };
      this.selectedRowKeys = [];
      this.loadCarriers({ page: 1, sort, status });
    },
    async onOpenValidity({ route, validity }) {
      if (this.openValidityId === validity.validityId) {
        this.closeValidityPanel();
        return;
      }

      if (this.newValidityPanel.saving) return;
      this.closeNewValidityPanel();
      this.panelSeq += 1;

      this.validityPanel = {
        visible: true,
        loading: true,
        routeId: route.routeId,
        validity,
        details: null,
      };

      try {
        const details = await this.fetchValidityDetails({
          freightTableId: this.freightTableId,
          routeId: route.routeId,
          validityId: validity.validityId,
        });

        if (
          this.validityPanel.visible &&
          this.validityPanel.validity.validityId === validity.validityId
        ) {
          this.validityPanel = { ...this.validityPanel, details, loading: false };
        }
      } catch (error) {
        this.validityPanel = { ...this.validityPanel, loading: false };
        handleApiError(
          this,
          error,
          "Não foi possível carregar os detalhes da vigência",
        );
      }
    },
    closeValidityPanel() {
      this.validityPanel = {
        visible: false,
        loading: false,
        routeId: null,
        validity: {},
        details: null,
      };
    },
    loadDetails() {
      this.fetchFreightTableDetails({
        freightTableId: this.freightTableId,
      }).catch((error) => {
        handleApiError(
          this,
          error,
          "Não foi possível carregar os detalhes da tabela de frete",
        );
      });
    },
    loadCarriers(payload) {
      this.carriersRequested = true;
      this.fetchCarriers({ freightTableId: this.freightTableId, ...payload })
        .catch((error) => {
          handleApiError(
            this,
            error,
            "Não foi possível carregar as transportadoras",
          );
        })
        .finally(() => {
          this.hasFetchedCarriers = true;
        });
    },
    onCarriersFilterChange({ search }) {
      this.carriersSearch = search;
      this.selectedRowKeys = [];
      this.loadCarriers({ page: 1, search });
    },
    onCarriersTableChange({ page, perPage }) {
      this.selectedRowKeys = [];
      const sizeChanged = perPage !== this.carriersPagination.perPage;
      this.loadCarriers({ page: sizeChanged ? 1 : page, perPage });
    },
    onCarriersBulkAction(key) {
      if (!this.selectedRowKeys.length) return;

      const active = key === "activate";
      const total = this.selectedRowKeys.length;
      const alvo =
        total === 1
          ? "a transportadora selecionada"
          : `as ${total} transportadoras selecionadas`;

      Modal.confirm({
        title: active ? "Ativar transportadoras" : "Inativar transportadoras",
        icon: (h) =>
          h("a-icon", {
            props: {
              type: active ? "info-circle" : "exclamation-circle",
              theme: "filled",
            },
          }),
        content: `Ao confirmar, você ${
          active ? "ativa" : "inativa"
        } ${alvo}. Deseja continuar?`,
        okText: active ? "Ativar" : "Inativar",
        cancelText: "Cancelar",
        class: "freight-tables-confirm",
        onOk: () => this.applyCarriersStatus(active),
      });
    },
    async applyCarriersStatus(active) {
      try {
        const result = await this.updateCarriersStatus({
          freightTableId: this.freightTableId,
          carrierIds: this.selectedRowKeys.slice(),
          active,
        });

        this.selectedRowKeys = [];
        this.$notification.success({
          message: "Sucesso",
          description:
            (result && result.message) ||
            (active
              ? "Transportadora(s) ativada(s) com sucesso!"
              : "Transportadora(s) inativada(s) com sucesso!"),
        });
      } catch (error) {
        handleApiError(
          this,
          error,
          active
            ? "Não foi possível ativar as transportadoras"
            : "Não foi possível inativar as transportadoras",
        );
      }
    },
    async onToggleCarrierStatus({ record, active }) {
      try {
        await this.updateCarrierStatus({
          freightTableId: this.freightTableId,
          carrierId: record.carrierId,
          active,
        });
      } catch (error) {
        handleApiError(
          this,
          error,
          "Não foi possível alterar o status da transportadora",
        );
      }
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.freight-table-details-page {
  display: flex;
  align-items: flex-start;
  gap: 24px;

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__panel-col {
    flex: 0 0 720px;
    align-self: stretch;
    min-width: 0;
  }
}

.freight-table-details {
  font-family: @font-primary;
  padding: 24px;

  &__spin {
    display: block;
    padding: 48px;
    text-align: center;
  }
}

.freight-table-history {
  &__filters {
    position: sticky;
    top: 0;
    z-index: 2;
    padding-top: 4px;
    background: @color-bg-surface;
  }

  &__loading,
  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 0;
  }

  &__group {
    margin-bottom: 20px;
  }

  &__date {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding-bottom: 12px;
    margin-bottom: 12px;
    border-bottom: 1px solid @color-border-light;
  }

  &__day {
    font-size: 15px;
    font-weight: 600;
    color: @color-text-dark;
  }

  &__year {
    font-size: 13px;
    color: @color-text-light;
  }

  &__item {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__item-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: @color-bg-subtle;
    font-size: 11px;
    font-weight: 600;
    color: @color-text-medium;
  }

  &__name {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 14px;
    font-weight: 600;
    color: @color-text-dark;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__time {
    flex: none;
    font-size: 12px;
    color: @color-text-light;
  }

  &__bubble {
    margin: 0 0 0 36px;
    padding: 10px 12px;
    border-radius: 8px;
    background: @color-bg-subtle;
    font-size: 14px;
    color: @color-text-medium;
  }

  &__more {
    margin-top: 8px;
  }

  &__tabs {
    margin-top: 32px;

    /deep/ .ant-tabs-nav .ant-tabs-tab {
      font-size: 14px;
      color: @color-text-medium;
      padding: 12px 0;
      margin-right: 32px;
    }

    /deep/ .ant-tabs-nav .ant-tabs-tab-active {
      color: @color-primary;
      font-weight: 500;
    }

    /deep/ .ant-tabs-ink-bar {
      background-color: @color-primary;
      height: 2px;
    }
  }
}
</style>
